import { createPlgAccount, fetchPlgMe } from "plg/api/plgAuth";
import { setPlgTokens } from "plg/auth/plgTokenStore";
import { asSignupMethod, trackPlg, trackSignupBlocked } from "plg/analytics/events";
import { emailDomain } from "plg/analytics/slugs";
import { identifyPlg, setupTraits } from "plg/analytics/identity";
import { rememberSignupBlocked } from "plg/utils/signupBlocked";
import { clearSignupSession, saveSignupSession, signupTokenClaims } from "plg/utils/signupSession";
import type { SignupSession } from "plg/utils/signupSession";
import type { SignupAnswers } from "plg/types";
import { PLG_SIGNUP_ROUTE } from "constants/routes";

/** Why the first step could not create the account: a redirect, or a message shown on the step. */
export type CreateAccountResult =
  | { ok: true }
  | { ok: false; redirect: string }
  | { ok: false; message: string };

const MESSAGES = {
  exists: "You already have an account. Sign in again to continue.",
  adminExists: "You already have a portal account.",
  ipLimit: "We can't create another account from this network today. Please try again tomorrow.",
  failed: "We could not create your account. Nothing was lost; please try again.",
};

/**
 * The first setup step's Continue: creates the account from its answers and signs the customer in, so the
 * later steps are saved as they go and a sign-in resumes them. The sign-up token is no longer needed after.
 */
export async function createAccountFromFirstStep(
  session: Pick<SignupSession, "prefill" | "enriched"> & { signupToken: string },
  answers: SignupAnswers
): Promise<CreateAccountResult> {
  const res = await createPlgAccount({ signupToken: session.signupToken, answers });
  const claims = signupTokenClaims(session.signupToken);
  const method = asSignupMethod(claims.provider);
  const domain = emailDomain(claims.email);

  if (res.ok === true) {
    const profile = setPlgTokens(res.data);
    if (!profile) return { ok: false, message: MESSAGES.failed };
    trackPlg("account_created", { method, email_type: "business" });
    identifyPlg(profile, setupTraits(answers));
    fetchPlgMe().catch(() => undefined);
    saveSignupSession({ prefill: session.prefill, enriched: session.enriched, answers });
    return { ok: true };
  }

  if (res.code === "ACCOUNT_BLOCKED") {
    // Public email domain: the account exists but waits for Edge's review.
    trackPlg("account_created", { method, email_type: "personal" });
    trackPlg("signup_blocked", { reason: "personal_email", ...(domain ? { email_domain: domain } : {}) });
    rememberSignupBlocked();
    clearSignupSession();
    return { ok: false, redirect: `${PLG_SIGNUP_ROUTE}?reason=blocked` };
  }
  if (res.code !== "COUNTRY_NOT_SUPPORTED" && res.status !== 401) {
    trackSignupBlocked(res.code, domain);
    trackPlg("error_shown", { error_type: res.code || "generic", screen: "account_setup" });
  }
  if (res.status === 401) {
    clearSignupSession();
    return { ok: false, redirect: `${PLG_SIGNUP_ROUTE}?expired=1` };
  }
  if (res.code === "COUNTRY_NOT_SUPPORTED") {
    clearSignupSession();
    return { ok: false, redirect: `${PLG_SIGNUP_ROUTE}?reason=country` };
  }
  if (res.code === "SIGNUP_IP_LIMIT") return { ok: false, message: MESSAGES.ipLimit };
  if (res.code === "ADMIN_ACCOUNT_EXISTS") {
    clearSignupSession();
    return { ok: false, message: MESSAGES.adminExists };
  }
  if (res.code === "ACCOUNT_EXISTS" || res.status === 409) {
    clearSignupSession();
    return { ok: false, message: MESSAGES.exists };
  }
  return { ok: false, message: MESSAGES.failed };
}
